import { Conditional, Menu } from "@result/rui/components";
import { classNames } from "@result/rui/utils/component";

import MainNavigation from "#/components/MainNavigation";

export const MENU_STATE_COMPACT = "compact";
export const MENU_STATE_EXPANDED = "expanded";

/**
 * The width from which the sidebar is the permanent vertical rail — kept in
 * sync with `$menu-rail-breakpoint` in `scss/_menu.scss`. It sits at the tablet
 * range, not at desktop, so a tablet keeps the same menu in portrait as in
 * landscape. Below it the navbar becomes the app ribbon (burger, page location,
 * account) and the sidebar is reduced to the drawer it opens.
 */
export const RAIL_BREAKPOINT_PX = 768;

/**
 * A phone on its side is as wide as a tablet upright, so the rail also asks for
 * height — no phone in landscape reaches this, no tablet in landscape misses
 * it. Rotating a phone therefore leaves the burger and drawer exactly where
 * they were. Kept in sync with `$menu-rail-min-height` in `scss/_menu.scss`.
 */
export const RAIL_MIN_HEIGHT_PX = 600;

/** True when the layout has the permanent sidebar rail rather than the drawer. */
export function hasSidebarRail() {
  return (
    window.innerWidth >= RAIL_BREAKPOINT_PX &&
    window.innerHeight >= RAIL_MIN_HEIGHT_PX
  );
}

/**
 * The width from which the rui layout grid gives the sidebar a column of its
 * own, so the expanded rail can push the page rather than overlay it — kept in
 * sync with `$menu-grid-breakpoint` in `scss/_menu.scss`.
 */
export const DESKTOP_BREAKPOINT_PX = 1024;

/**
 * A tablet in landscape is as wide as a laptop, so width alone cannot tell the
 * two apart — the primary pointer can. Starting expanded therefore asks for
 * both: a viewport with a column wide enough for the expanded rail, and a mouse
 * driving it. A tablet is collapsed in either orientation, which keeps a
 * rotation from changing the menu underneath the user.
 */
export function startsExpanded() {
  return (
    hasSidebarRail() &&
    window.innerWidth >= DESKTOP_BREAKPOINT_PX &&
    (window.matchMedia?.("(pointer: fine)")?.matches ?? false)
  );
}

/**
 * Desktop opens with the rail expanded, as it always has. A tablet has the rail
 * too but starts it collapsed to icons, and a phone has no rail to show until
 * the burger opens the drawer — both are the collapsed state.
 */
export function initialMenuState() {
  return startsExpanded() ? MENU_STATE_EXPANDED : MENU_STATE_COMPACT;
}

/**
 * Menu state is owned by MainLayout because the narrow-layout toggle lives in
 * the navbar, not in here.
 *
 * @param {object} props
 * @param {string} props.menuState - MENU_STATE_COMPACT | MENU_STATE_EXPANDED
 * @param {Function} props.setMenuState
 */
export default function LeftSidebar({ menuState, setMenuState }) {
  const isExpanded = menuState === MENU_STATE_EXPANDED;
  return (
    <>
      <Menu
        menuState={menuState}
        isHoverable={false}
        className={classNames("left-sidebar has-background-primary", {
          "is-expanded": isExpanded,
        })}>
        <MainNavigation menuState={menuState} setMenuState={setMenuState} />
      </Menu>
      <Conditional condition={isExpanded}>
        {/* Drawer backdrop — only visible below the desktop breakpoint. */}
        <div
          className="left-sidebar-backdrop"
          onClick={() => setMenuState(MENU_STATE_COMPACT)}
        />
      </Conditional>
    </>
  );
}
